import { Auth, Carto, Package } from '.'
import { Basemap, BasemapOption } from './basemap.type'

export enum ExternalLinks {
  Help = 'help',
  TermsOfUse = 'terms_of_use',
  PrivacyStatement = 'privacy_statement',
}

export interface Language {
  id: string
  name: string
  locale?: string
}

export interface Languages {
  enable: boolean
  options: Language[]
}

export interface LocalStorageKeys {
  lang: string
  auth: string
  firebase_token: string
}

export interface Units {
  distance: string
  currency: string
  currency_symbol: string
}

export interface FeatureAudit {
  enable: boolean
}

export interface FeatureDashboard {
  show_others_groups_tab: boolean
  show_public_plans_by_country_and_user: boolean
}

export interface SharePlanExpiracy {
  enabled: boolean
  defaultExpirationInDays: number
}

export interface FeatureShare {
  enable: ConfigByEnvironment<boolean>
  retrieve_in_blocks_of: number
  expiracyOffset: number
  password: boolean
  password_is_required: boolean
  passwordValidation: (pass: string) => boolean
  package?: {
    showPrice: boolean
  }
  export_enabled: boolean
  expiracy: SharePlanExpiracy
  feedback_email: string
  hide_unused_filter_widgets: boolean
  feedback_view: {
    show_emoji: boolean
    show_sub_message: boolean
    show_link: boolean
  }
}

export interface FeatureNewPlan {
  enable: boolean
}

export interface FeatureSavePlan {
  enable_save_as: boolean
  enable: boolean
}

export interface MappingField {
  model: 'assets'
  modelField: string
  exportField: string
  // eslint-disable-next-line @typescript-eslint/ban-types
  formatFunction?: Function
  avoidApplyingNumberFormat?: boolean
  avoidInSharedPlan?: boolean
}

export interface FeatureExport {
  enable: boolean
  include_total_assets_column: boolean
  template_file: string | null
  template_file_test: string | null
  fields_mapping: MappingField[]
  columnsTranslation: {
    enabled: boolean
    defaultCountryId?: number
  }
}

export interface FeaturePopup {
  title_i18n: string
  title_id_field: string
  subtitle_id_field: string
  show_picture: boolean
  show_address: boolean
  show_streetview: boolean
  show_coordinates: boolean
  show_facing_direction: boolean
  show_angle_to_map_north: boolean
  show_panel_name: boolean
  show_type: boolean
  show_impressions: boolean
  show_exclude: boolean
  show_details: boolean
  shared_plan_show_details: boolean
  show_isoline: boolean
  show_description?: boolean
}

export interface ExternalLink {
  enable: boolean
  url: string
}

export type FeatureHelp = ExternalLink

export type TermsOfUse = ExternalLink

export type PrivacyStatement = ExternalLink

export interface MultiTabsFilters {
  enable: boolean
  max_tabs: number
}

export interface ViewState {
  longitude: number
  latitude: number
  zoom: number
  bearing: number
  pitch: number
}

export interface Legend {
  colors: number[]
  enable_product_selection: boolean
  default_product_selection: string
  facing_direction: boolean
  angle_to_map_north: boolean
  show_geoboundaries_layer: boolean
  show_product_selection: boolean
  title: string
  show_assets_ids_layer: boolean
}

export interface Map {
  layers_precedence: string[]
  asset_details_over_map: boolean
  fixed_cluster_size: number
  zoom_hide_cluster: number
  view_state: ViewState
  max_bbox: number[]
  legend: Legend
  highlightSelectedMarker: boolean
  screenshot_btn: boolean
}

/**
 * @deprecated Use ConfigByEnvironmentDatasetNormalized instead.
 * This interface uses legacy environment naming (prod/staging) while
 * ConfigByEnvironmentDatasetNormalized uses standardized naming (pro/stg)
 */

export interface ConfigByEnvironment<T> {
  dev: T
  staging: T
  prod: T
  local?: T
}
export interface ConfigByEnvironmentDatasetNormalized<T> {
  dev: T
  stg: T
  pro: T
  local?: T
}

/**
 * @deprecated Use ConfigByEnvironmentDatasetNormalized instead.
 * This interface uses legacy environment naming (prod/staging) while
 * ConfigByEnvironmentDatasetNormalized uses standardized naming (pro/stg)
 */
export type EnableByEnvironment = ConfigByEnvironment<boolean>
export type EnableByEnvironmentNormalized =
  ConfigByEnvironmentDatasetNormalized<boolean>

/**
 * @deprecated Use DataByEnvironmentNormalized instead.
 * This interface uses legacy environment naming (prod/staging) while
 * DataByEnvironmentNormalized uses standardized naming (pro/stg)
 */
export interface DataByEnvironment {
  dev: string
  staging: string
  prod: string
}

export type DataByEnvironmentNormalized =
  ConfigByEnvironmentDatasetNormalized<string>

export interface Widgets {
  show_button_tooltip_as_copies: boolean
}

export interface FeatureAssets {
  skip_popup_and_go_to_sidebar?: boolean
  show_sidebar_when_double_click_on_list?: boolean
}

export interface CustomGeoboundaries {
  enable: boolean
  upload_file: boolean
  upload_file_max_size_mb: number
  save_group_from_plan: boolean
}

export interface DashboardColumns {
  locale_keys: string[]
  model_accessors: string[]
}

export interface FeaturesPlan {
  realtime: {
    enable: boolean
    reaction_in_seconds: number
  }
  dashboard_columns: DashboardColumns
  save_brand: boolean
  save_client: boolean
  enable_quick_access_to_visibility: boolean
  default_save_as_public: boolean
}

export interface FeaturesPackages {
  enable: boolean
  availability_range: {
    value: number
    text: string
    color: string
    description: string
  }[]
  states_style: {
    [key in Package.State]: {
      background: string
      foreground: string
    }
  }
  dashboard_columns: DashboardColumns
  expiration: {
    booking_confirmation: number
    cancellation_time_limit: number
  }
  featured_packages: FeatureFeaturedPackages
}

export interface FeatureComments {
  enable: boolean
  attachments: {
    enable: boolean
    max_size_mb: number
  }
  show_toolbar_summary_plans_comments: boolean
  realtime_reaction_in_seconds: number
}

export interface FeatureLegal {
  terms_of_use: TermsOfUse
  privacy_statement: PrivacyStatement
  copyright: boolean
  trademarks: boolean
}

export interface FeaturedPackagesLock {
  expiration_interval: number
  renewal_interval: number
}

export interface FeatureFeaturedPackages {
  enabled: EnableByEnvironmentNormalized
  lock: FeaturedPackagesLock
}

export interface Sidepanel {
  back_list_action_size_small: boolean
  multi_tabs_filters: MultiTabsFilters
}

export interface Features {
  permission_resolver: Auth.PermissionResolver
  comments: FeatureComments
  packages: FeaturesPackages
  plan: FeaturesPlan
  assets?: FeatureAssets
  audit: FeatureAudit
  dashboard: FeatureDashboard
  share: FeatureShare
  new_plan: FeatureNewPlan
  save_plan: FeatureSavePlan
  export: FeatureExport
  popup: FeaturePopup
  popup_minimal: FeaturePopup
  popup_share: FeaturePopup
  help: FeatureHelp
  legal: FeatureLegal
  map: Map
  widgets: Widgets
  custom_geoboundaries: CustomGeoboundaries
  sidepanel: Sidepanel
}

export interface PlanFiltersWidgetCountries {
  enable: boolean
  default_selection_iso3: string[]
}

export interface PlanFiltersWidgetAssets {
  enable: boolean
  enable_only_admin: boolean
  xlsx_column_name: string
  example_file: string
  upload_by_file: boolean
  upload_by_paste: boolean
  default_option: 'file' | 'paste'
}

export interface PlanFiltersWidgetGeoBoundariesCustomDrawing {
  enable: boolean
  polygon: boolean
  circle: boolean
  route: boolean
}

export interface PlanFiltersWidgetGeoBoundariesSamples {
  name: string
  link: string
}

export interface PlanFiltersWidgetGeoBoundaries {
  enable: boolean
  permit_exclude: boolean
  custom_drawing: PlanFiltersWidgetGeoBoundariesCustomDrawing
  sample_files: PlanFiltersWidgetGeoBoundariesSamples[]
}

export interface PlanFiltersWidgetMediaTypeTypes {
  l1panelclass: string
  l2product: string
  l3subproduct?: string
  l4?: string
  l5?: string
  l6?: string
  l7?: string
  l8?: string
  l9?: string
}

export interface PlanFiltersWidgetMediaType {
  enable: boolean
  clear_action_acts_on_digital_filter: boolean
  filter_digital: boolean
  types: PlanFiltersWidgetMediaTypeTypes
  intelligent_digital_static_filter: boolean
}

export interface PlanFiltersWidgetPackages {
  enable: boolean
  price_range: [number, number]
}

export interface PlanFilters {
  widget_countries: PlanFiltersWidgetCountries
  widget_assets: PlanFiltersWidgetAssets
  widget_geoboundaries: PlanFiltersWidgetGeoBoundaries
  widget_media_type: PlanFiltersWidgetMediaType
  widget_packages: PlanFiltersWidgetPackages
}

export enum DataModelFieldTypes {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Date = 'date',
  Array = 'array',
  Geography = 'geography',
  ArrayNumbers = 'array_numbers',
  ArrayStrings = 'array_strings',
}

export interface DataModelField {
  id: string
  type: DataModelFieldTypes
  visible: boolean
  filterable: boolean
  display: boolean
  startup?: boolean
  background?: boolean
}

export interface DataModelFieldAutocomplete extends DataModelField {
  autocomplete: boolean
}

export interface CacheModel {
  dbName: string
  store: string
  key: string
  key_last_update: string
  version: number
}
export interface DataModelBase {
  table_name: string
  cache?: CacheModel
  columns: DataModelField[] | DataModelFieldAutocomplete[]
  chunk_size?: number
}

export interface AssetsDataModel extends DataModelBase {
  cluster_icon: string
  marker_icon: string
}

export interface DataModelWithTileSet {
  tileset: string
}

export interface Autocomplete {
  autocomplete_locations: string
  autocomplete: string
}

export interface Region {
  id: number
  name: string
  order: number
  countries: number[]
  geom: boolean
}
export interface RegionsHierarchy {
  region_1: Region
  region_2: Region
  region_3: Region
  region_4: Region
  region_5: Region
  region_6: Region
  region_7: Region
  region_8?: Region
  region_9?: Region
  region_10?: Region
  region_11?: Region
  region_12?: Region
  region_13?: Region
  region_14?: Region
  region_15?: Region
  region_16?: Region
  region_17?: Region
  region_18?: Region
}
export interface DataModelRegions extends DataModelBase {
  partitioned_column: string
  regions_hierarchy: RegionsHierarchy
}

export interface DataModel {
  regions: DataModelRegions
  assets: AssetsDataModel
  panelcounts: DataModelBase
  shared_plans_assets: DataModelBase
  custom_geoboundaries: DataModelBase
  packages: DataModelBase
  packages_availability: DataModelBase
}

export interface MetaRegion {
  type: string
  name: string
}

export interface MetaAssets {
  type: string
  name: string
  hide?: boolean
}

export interface Country {
  iso3: string
  iso2: string
  name: string
  id: number
  id_legacy?: number
  meta_region?: MetaRegion[]
  meta_assets?: MetaAssets[]
  carto_me_value?: string
  geocoder?: string
}

export enum RemoteFunction {
  FirebaseToken = 'firebase_token',
  Audit = 'audit',
  SharedPlan = 'share_plan',
  Auth0 = 'auth0',
  CustomGeoboundaries = 'custom_geoboundaries',
  SignedUrls = 'signed_urls',
  Geocoding = 'geocoding',
}

export interface RemoteFunctions {
  firebase_token: ConfigByEnvironment<string>
  audit: ConfigByEnvironment<string>
  share_plan: ConfigByEnvironment<string>
  auth0?: ConfigByEnvironment<string>
  custom_geoboundaries: ConfigByEnvironment<string>
  signed_urls?: ConfigByEnvironment<string>
  geocoding?: ConfigByEnvironment<string>
  featured_packages?: ConfigByEnvironment<string>
}

export interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

export interface BasemapsOptions {
  enable: boolean
  default: Basemap
  options: BasemapOption[]
}

export interface AppConfig {
  carto_client_id: ConfigByEnvironment<string>
  front_url: string[]
  carto_organization?: string
  carto_organization_required_for_testing: boolean
  firebase: ConfigByEnvironment<FirebaseConfig>
  remote_functions: RemoteFunctions
  buckets?: {
    attachments?: ConfigByEnvironment<string>
    featuredPackageImages?: ConfigByEnvironment<string>
  }
  basemaps: BasemapsOptions
}

export interface AppRole {
  token_property: keyof Carto.CartoMe
  value: string
  role: Auth.Role
}

export interface LoginPageSetup {
  logo?: string
  slogan_message?: string
  welcome_message?: string
  contact_message?: string
  contact_email?: string
}

export interface Maintenance {
  enabled: boolean
  until: string
  domains: string[]
}

export interface AppMetadata {
  login_page_setup?: LoginPageSetup
  flavour: string
  maintenance?: Maintenance
  app_title: string
  logo: string
  app_config: AppConfig
  roles: AppRole[]
  auth0_user_key: string
  auth0_account_id_key: string
  base_url: string
  bq_project: ConfigByEnvironment<string>
  carto_events_url: string
  send_carto_events: {
    enabled: ConfigByEnvironmentDatasetNormalized<boolean>
  }
  connection: string
  analytics_toolbox_base_url: string
  languages: Languages
  local_storage_keys: LocalStorageKeys
  units: Units
  data_model: DataModel
  features: Features
  plan_filters: PlanFilters
  countries: Country[]
}
